import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';

interface Question {
  q: string;
  steps: string[];
  answer: string;
}

interface Skill {
  id: string;
  title: string;
  standards: string[];
  explanation: string;
  questions: Question[];
}

interface Topic {
  id: string;
  title: string;
  icon: string;
  color: string;
  skills: Skill[];
}

interface GradeLessonPageProps {
  grade: string;
  gradeLabel: string;
  gradeColor: string;
  topics: Topic[];
  prevGrade?: { label: string; path: string };
  nextGrade?: { label: string; path: string };
  canonicalPath?: string;
}

/** Maps the grade/gradeLabel to the correct canonical URL path */
function resolveCanonicalPath(grade: string, gradeLabel: string, override?: string): string {
  if (override) return override;

  const MAP: Record<string, string> = {
    'Kindergarten': '/grade/k',
    'Grade 1': '/grade/1',
    'Grade 2': '/grade/2',
    'Grade 3': '/grade/3',
    'Grade 4': '/grade/4',
    'Grade 5': '/grade/5',
    'Grade 6': '/grade/6',
    'Grade 7': '/grade/7',
    'Grade 8': '/grade/8',
    'Grade 9 / Algebra 1': '/grade/9',
    'Grade 9': '/grade/9',
    'Grade 10': '/grade/10',
    'Grade 11': '/grade/11',
    'Algebra 1': '/algebra1',
    'Algebra 2': '/algebra2',
    'Geometry': '/geometry',
    'Pre-Calculus': '/precalc',
    'AP Calculus AB': '/ap-calc-ab',
    'AP Calculus BC': '/ap-calc-bc',
    'AP Statistics': '/ap-statistics',
  };

  return MAP[grade] ?? MAP[gradeLabel] ?? `/${grade.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/* --------------------------------------------------------------- */
/*  Colour map – unchanged                                            */
/* --------------------------------------------------------------- */
const colorMap: Record<
  string,
  {
    bg: string;
    text: string;
    border: string;
    badge: string;
    btn: string;
    light: string;
  }
> = {
  orange: {
    bg: 'bg-orange-600',
    text: 'text-orange-600',
    border: 'border-orange-600',
    badge: 'bg-orange-100 text-orange-700',
    btn: 'bg-orange-600 hover:bg-orange-700',
    light: 'bg-orange-50',
  },
  teal: {
    bg: 'bg-teal-600',
    text: 'text-teal-600',
    border: 'border-teal-600',
    badge: 'bg-teal-100 text-teal-700',
    btn: 'bg-teal-600 hover:bg-teal-700',
    light: 'bg-teal-50',
  },
  amber: {
    bg: 'bg-amber-600',
    text: 'text-amber-600',
    border: 'border-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    btn: 'bg-amber-600 hover:bg-amber-700',
    light: 'bg-amber-50',
  },
  green: {
    bg: 'bg-green-600',
    text: 'text-green-600',
    border: 'border-green-600',
    badge: 'bg-green-100 text-green-700',
    btn: 'bg-green-600 hover:bg-green-700',
    light: 'bg-green-50',
  },
  rose: {
    bg: 'bg-rose-600',
    text: 'text-rose-600',
    border: 'border-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    btn: 'bg-rose-600 hover:bg-rose-700',
    light: 'bg-rose-50',
  },
  violet: {
    bg: 'bg-violet-600',
    text: 'text-violet-600',
    border: 'border-violet-600',
    badge: 'bg-violet-100 text-violet-700',
    btn: 'bg-violet-600 hover:bg-violet-700',
    light: 'bg-violet-50',
  },
};

/* --------------------------------------------------------------- */
/*  QuestionCard – unchanged                                         */
/* --------------------------------------------------------------- */
function QuestionCard({
  question,
  index,
}: {
  question: Question;
  index: number;
}) {
  const [showSteps, setShowSteps] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-7 h-7 flex items-center justify-center bg-slate-900 text-white rounded-full text-xs font-bold flex-shrink-0">
          {index + 1}
        </span>
        <p className="text-gray-800 font-medium text-sm leading-relaxed">
          {question.q}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap ml-10">
        <button
          onClick={() => {
            setShowSteps(!showSteps);
            if (!showSteps) setShowAnswer(false);
          }}
          className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          {showSteps ? 'Hide Steps' : 'Show Steps'}
        </button>

        <button
          onClick={() => setShowAnswer(!showAnswer)}
          className="px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          {showAnswer ? 'Hide Answer' : 'Show Answer'}
        </button>
      </div>

      {showSteps && (
        <div className="mt-3 ml-10 bg-slate-50 rounded-lg p-4 border-l-4 border-slate-400">
          <p className="text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">
            Step-by-Step Solution
          </p>
          <ol className="space-y-1.5">
            {question.steps.map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-slate-700"
              >
                <span className="w-5 h-5 flex items-center justify-center bg-slate-200 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {showAnswer && (
        <div className="mt-3 ml-10 bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
          <p className="text-xs font-bold text-green-700 mb-1">Answer</p>
          <p className="text-sm font-bold text-green-800">{question.answer}</p>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- */
/*  SkillSection – now loads worksheets with useEffect            */
/* --------------------------------------------------------------- */
function SkillSection({
  skill,
  topicColor,
  grade,
  topicTitle,
}: {
  skill: Skill;
  topicColor: string;
  grade: string;
  topicTitle: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [worksheets, setWorksheets] = useState<
    | {
        id: string;
        title: string;
        topic: string;
        thumbnailUrl: string;
        difficulty: string;
        pages: number;
        isFree: boolean;
        grade: string;
      }[]
    | null
  >(null);
  const [loadingWorksheets, setLoadingWorksheets] = useState(false);
  const [worksheetError, setWorksheetError] = useState<string | null>(null);

  const colors = colorMap[topicColor] || colorMap.orange;
  const displayedQuestions = showAllQuestions
    ? skill.questions
    : skill.questions.slice(0, 5);

  /* ----------------------------------------------------------- */
  /*  Dynamically import worksheet data when the component mounts */
  /* ----------------------------------------------------------- */
  useEffect(() => {
    let isMounted = true;

    const loadWorksheets = async () => {
      setLoadingWorksheets(true);
      try {
        // The dynamic import returns a module; we extract the named export.
        const module = await import('../../worksheets/all-worksheets');
        const allWorksheets = module?.allWorksheets ?? [];

        // Filter worksheets that match the current skill/grade.
        const relevantWorksheets = allWorksheets
          .filter(
            (w) =>
              !w.isFree &&
              w.grade === grade &&
              (w.topic
                .toLowerCase()
                .includes(skill.title.toLowerCase().split(' ')[0]) ||
                skill.title
                  .toLowerCase()
                  .includes(w.topic.toLowerCase()))
          )
          .slice(0, 4);

        if (isMounted) setWorksheets(relevantWorksheets);
      } catch (err) {
        console.error('Failed to load worksheets:', err);
        if (isMounted) setWorksheetError('Unable to load worksheets.');
      } finally {
        if (isMounted) setLoadingWorksheets(false);
      }
    };

    loadWorksheets();

    return () => {
      isMounted = false;
    };
  }, [grade, skill.title]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all">
      {/* Header – collapsible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div
            className={`w-10 h-10 flex items-center justify-center ${colors.bg} rounded-xl flex-shrink-0`}
          >
            <i className="ri-book-open-line text-white text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-gray-900 mb-1">
              {skill.title}
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {skill.standards.slice(0, 3).map((std, i) => (
                <span
                  key={i}
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge}`}
                >
                  {std}
                </span>
              ))}
              {skill.standards.length > 3 && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                  +{skill.standards.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={`w-8 h-8 flex items-center justify-center rounded-full ${colors.light} flex-shrink-0 ml-4`}
        >
          <i
            className={`${
              expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            } ${colors.text} text-xl`}
          ></i>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100">
          {/* All Standards */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Standards Covered
            </p>
            <div className="flex flex-wrap gap-1.5">
              {skill.standards.map((std, i) => (
                <span
                  key={i}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${colors.badge}`}
                >
                  {std}
                </span>
              ))}
            </div>
          </div>

          {/* Explanation */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div
                className={`w-6 h-6 flex items-center justify-center ${colors.bg} rounded-lg`}
              >
                <i className="ri-lightbulb-line text-white text-sm"></i>
              </div>
              <p className="text-sm font-bold text-gray-800">
                Lesson Explanation
              </p>
            </div>
            <div
              className={`${colors.light} rounded-xl p-4 border-l-4 ${colors.border}`}
            >
              <p className="text-sm text-gray-700 leading-relaxed">
                {skill.explanation}
              </p>
            </div>
          </div>

          {/* Questions */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 flex items-center justify-center ${colors.bg} rounded-lg`}
                >
                  <i className="ri-question-line text-white text-sm"></i>
                </div>
                <p className="text-sm font-bold text-gray-800">
                  Practice Questions ({skill.questions.length} total)
                </p>
              </div>
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-bold ${colors.badge}`}
              >
                {showAllQuestions
                  ? skill.questions.length
                  : Math.min(5, skill.questions.length)}{' '}
                shown
              </span>
            </div>

            <div className="space-y-3">
              {displayedQuestions.map((q, i) => (
                <QuestionCard key={i} question={q} index={i} />
              ))}
            </div>

            {skill.questions.length > 5 && (
              <button
                onClick={() => setShowAllQuestions(!showAllQuestions)}
                className={`mt-4 w-full py-3 rounded-xl text-sm font-bold ${colors.btn} text-white transition-all cursor-pointer whitespace-nowrap`}
              >
                {showAllQuestions
                  ? 'Show Fewer Questions'
                  : `Show All ${skill.questions.length} Questions with Solutions`}
              </button>
            )}
          </div>

          {/* Practice Worksheets Section */}
          {loadingWorksheets && (
            <div className="px-6 py-5 text-center text-gray-500">
              Loading worksheets…
            </div>
          )}

          {worksheetError && (
            <div className="px-6 py-5 text-center text-red-600">
              {worksheetError}
            </div>
          )}

          {worksheets && worksheets.length > 0 && (
            <div className="px-6 py-5 bg-gradient-to-br from-teal-50 to-orange-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-lg">
                    <i className="ri-file-text-line text-white text-sm"></i>
                  </div>
                  <p className="text-sm font-bold text-gray-800">
                    Practice Worksheets
                  </p>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-orange-100 text-orange-700">
                  Premium
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {worksheets.map((worksheet) => (
                  <Link
                    key={worksheet.id}
                    to={`/worksheets?grade=${encodeURIComponent(
                      grade
                    )}&topic=${encodeURIComponent(worksheet.topic)}`}
                    className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all cursor-pointer group"
                  >
                    <img
                      src={worksheet.thumbnailUrl}
                      alt={worksheet.title}
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1 group-hover:text-teal-600 transition-colors">
                        {worksheet.title}
                      </h5>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            worksheet.difficulty === 'Beginner'
                              ? 'bg-green-100 text-green-700'
                              : worksheet.difficulty === 'Intermediate'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {worksheet.difficulty}
                        </span>
                        <span className="text-xs text-gray-500">
                          {worksheet.pages} pages
                        </span>
                      </div>
                    </div>
                    <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg group-hover:bg-teal-600 transition-colors flex-shrink-0">
                      <i className="ri-arrow-right-line text-teal-600 group-hover:text-white text-sm transition-colors"></i>
                    </div>
                  </Link>
                ))}
              </div>

              <Link
                to={`/worksheets?grade=${encodeURIComponent(grade)}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-folder-open-line"></i>
                View All Worksheets for {grade}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- */
/*  TopicSection – unchanged                                         */
/* --------------------------------------------------------------- */
function TopicSection({ topic, grade }: { topic: Topic; grade: string }) {
  const [expanded, setExpanded] = useState(true);
  const colors = colorMap[topic.color] || colorMap.orange;
  const totalQuestions = topic.skills.reduce(
    (sum, s) => sum + s.questions.length,
    0
  );

  return (
    <div className="mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-5 rounded-2xl ${colors.bg} text-white cursor-pointer hover:opacity-95 transition-opacity`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
            <i className={`${topic.icon} text-2xl`}></i>
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold">{topic.title}</h3>
            <p className="text-white/80 text-sm">
              {topic.skills.length} skills · {totalQuestions} practice
              questions
            </p>
          </div>
        </div>
        <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
          <i
            className={`${
              expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            } text-2xl`}
          ></i>
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4 pl-2">
          {topic.skills.map((skill) => (
            <SkillSection
              key={skill.id}
              skill={skill}
              topicColor={topic.color}
              grade={grade}
              topicTitle={topic.title}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- */
/*  Main Page Component – unchanged                                   */
/* --------------------------------------------------------------- */
export default function GradeLessonPage({
  grade,
  gradeLabel,
  gradeColor,
  topics,
  prevGrade,
  nextGrade,
  canonicalPath,
}: GradeLessonPageProps) {
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const totalSkills = topics.reduce((sum, t) => sum + t.skills.length, 0);
  const totalQuestions = topics.reduce(
    (sum, t) => t.skills.reduce((s2, sk) => s2 + sk.questions.length, sum),
    0
  );
  const colors = colorMap[gradeColor] || colorMap.orange;

  const scrollToTopic = (id: string) => {
    setActiveTopicId(id);
    const el = document.getElementById(`topic-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const seoPath = resolveCanonicalPath(grade, gradeLabel, canonicalPath);
  const seoTitle = `${gradeLabel} Mathematics | Full Curriculum & Practice | Math Mastery`;
  const seoDescription = `Complete ${gradeLabel} mathematics curriculum with ${topics.length} topics, ${totalSkills} skills, and ${totalQuestions}+ practice questions. Step-by-step solutions aligned to Common Core, NCTM, NY, OH, TX standards.`;
  const seoKeywords = `${gradeLabel}, ${gradeLabel} math, ${gradeLabel} curriculum, ${gradeLabel} practice problems, math worksheets`;

  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${gradeLabel} Mathematics`,
    description: seoDescription,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'Math Mastery',
    },
    educationalLevel: gradeLabel,
    teaches: topics.map((t) => t.title),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalPath={seoPath}
        schema={courseSchema}
      />

      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/ed58018d-bb73-4bb8-8590-7096e85d7af4.png"
              alt="Math Mastery"
              className="h-10 w-10 object-contain"
            />
            <span className="text-lg font-bold text-gray-900">
              Math Mastery
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {prevGrade && (
              <Link
                to={prevGrade.path}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> {prevGrade.label}
              </Link>
            )}
            <span
              className={`px-4 py-2 text-sm font-bold ${colors.bg} text-white rounded-lg whitespace-nowrap`}
            >
              {gradeLabel}
            </span>
            {nextGrade && (
              <Link
                to={nextGrade.path}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                {nextGrade.label} <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={`pt-24 pb-12 ${colors.bg} text-white`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> Back to All Grades
              </Link>
              <h1 className="text-4xl lg:text-5xl font-bold mb-3">
                {gradeLabel} Mathematics
              </h1>
              <p className="text-white/80 text-lg max-w-2xl">
                Complete curriculum with Common Core, NCTM, NY, OH, PA, VA,
                TX standards — every subtopic, every essential skill, with 30
                questions and step-by-step solutions each.
              </p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              <div className="bg-white/20 rounded-2xl p-5 text-center min-w-[100px]">
                <div className="text-3xl font-bold">{topics.length}</div>
                <div className="text-white/80 text-sm mt-1">Topics</div>
              </div>
              <div className="bg-white/20 rounded-2xl p-5 text-center min-w-[100px]">
                <div className="text-3xl font-bold">{totalSkills}</div>
                <div className="text-white/80 text-sm mt-1">Skills</div>
              </div>
              <div className="bg-white/20 rounded-2xl p-5 text-center min-w-[100px]">
                <div className="text-3xl font-bold">{totalQuestions}</div>
                <div className="text-white/80 text-sm mt-1">Questions</div>
              </div>
            </div>
          </div>

          {/* Standards badges */}
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              'Common Core',
              'NCTM',
              'New York',
              'Ohio',
              'Pennsylvania',
              'Virginia',
              'Texas',
            ].map((s) => (
              <span
                key={s}
                className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium whitespace-nowrap"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Topic Quick Nav */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap mr-2">
              Jump to:
            </span>
            {topics.map((topic) => {
              const c = colorMap[topic.color] || colorMap.orange;
              return (
                <button
                  key={topic.id}
                  onClick={() => scrollToTopic(topic.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeTopicId === topic.id
                      ? `${c.bg} text-white`
                      : `${c.badge} hover:opacity-80`
                  }`}
                >
                  <i className={`${topic.icon} mr-1`}></i>
                  {topic.title}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        {topics.map((topic) => (
          <div key={topic.id} id={`topic-${topic.id}`}>
            <TopicSection topic={topic} grade={grade} />
          </div>
        ))}

        {/* Grade Navigation */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {prevGrade && (
            <Link
              to={prevGrade.path}
              className="flex items-center gap-4 p-6 bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer group"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl group-hover:bg-orange-100 transition-colors">
                <i className="ri-arrow-left-line text-2xl text-gray-600 group-hover:text-orange-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Previous Grade
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {prevGrade.label}
                </p>
              </div>
            </Link>
          )}
          {nextGrade && (
            <Link
              to={nextGrade.path}
              className="flex items-center justify-between gap-4 p-6 bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer group md:col-start-2"
            >
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Next Grade
                </p>
                <p className="text-lg font-bold text-gray-900">
                  {nextGrade.label}
                </p>
              </div>
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl group-hover:bg-orange-100 transition-colors">
                <i className="ri-arrow-right-line text-2xl text-gray-600 group-hover:text-orange-600"></i>
              </div>
            </Link>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-amber-50 border-t border-amber-100 py-8 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/ed58018d-bb73-4bb8-8590-7096e85d7af4.png"
              alt="Math Mastery"
              className="h-10 w-10 object-contain"
            />
            <div>
              <p className="font-bold text-gray-900">Math Mastery</p>
              <p className="text-xs text-gray-500">
                by Augustine Anyimadu-Ahenkae
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 Augustine Anyimadu-Ahenkae. All rights reserved.
          </p>
          <Link
            to="/"
            className="px-5 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}
